/**
 * Share link section visibility — shared key list, types and defaults.
 *
 * EPIC-27 / PMA-PUB-014 / PMA-ADMIN-014 / PMA-COCK-186. `SHARE_SECTION_KEYS`
 * is the ONE exported list every consumer must reuse — the admin settings
 * page, the public share page, and (per the wave's locked API contract) the
 * backend's own enum. Never hand-duplicate this list; if a section is added
 * or renamed, this is the only file that needs to change on the frontend.
 *
 * Defaults are all `visible`, so shipping this feature with no admin-set
 * document changes nothing about how the public page renders today.
 */

export const SHARE_SECTION_KEYS = [
  "score_bar",
  "team_sheets",
  "timeline",
  "context_banner",
  "stats_comparison",
  "shot_map",
  "ineffective_breakdown",
  "recovery_banner",
  "momentum_chart",
  "heatmaps",
  "area_control",
  "top_players",
  "player_card",
] as const;

export type ShareSectionKey = (typeof SHARE_SECTION_KEYS)[number];

export type ShareVisibilityState = "visible" | "blurred" | "hidden";

export type ShareSectionMap = Record<ShareSectionKey, ShareVisibilityState>;

export interface ShareUpsell {
  message_es: string;
  message_en: string;
  banner_es: string;
  banner_en: string;
  url: string | null;
}

export interface ShareVisibility {
  sections: ShareSectionMap;
  upsell: ShareUpsell;
}

/**
 * Placeholder ES/EN copy (locked decision 5, 2026-09-12) until the client
 * supplies their own wording. Editable globally via the admin page — these
 * are only the client-side defaults used before any document exists or
 * while a key is absent from one.
 */
export const DEFAULT_UPSELL: ShareUpsell = {
  message_es:
    "Este análisis está disponible al contratar nuestros servicios. Contáctanos para desbloquearlo.",
  message_en:
    "This analysis is available when you hire our services. Contact us to unlock it.",
  banner_es:
    "Algunos análisis de este partido están disponibles al contratar nuestros servicios. Contáctanos para ver el informe completo.",
  banner_en:
    "Some analyses for this match are available when you hire our services. Contact us for the full report.",
  url: null,
};

function buildAllVisibleSections(): ShareSectionMap {
  return SHARE_SECTION_KEYS.reduce((acc, key) => {
    acc[key] = "visible";
    return acc;
  }, {} as ShareSectionMap);
}

export const DEFAULT_SHARE_VISIBILITY: ShareVisibility = {
  sections: buildAllVisibleSections(),
  upsell: { ...DEFAULT_UPSELL },
};

/**
 * Resolve one section's state from a (possibly partial or absent) map.
 * A missing key — an older/partial document, or a key added after the
 * document was last saved — reads as `visible` (the non-breaking default).
 */
export function resolveSectionState(
  map: Partial<ShareSectionMap> | null | undefined,
  key: ShareSectionKey,
): ShareVisibilityState {
  return map?.[key] ?? "visible";
}

/** True when at least one section is `blurred` or `hidden`. */
export function hasRestrictedSection(
  map: Partial<ShareSectionMap> | null | undefined,
): boolean {
  if (!map) return false;
  return SHARE_SECTION_KEYS.some((key) => {
    const state = map[key];
    return state === "blurred" || state === "hidden";
  });
}
